import {
	type VideoProject,
	VideoProjectSchema,
	type VideoProjectStatusType,
	VideoProjectStatusSchema,
	buildEpisodeSummary,
	narratorAvatarTimingFromProfile,
	narratorConfigFromProfile,
	nextStoryEpisodeNumber,
	parseStoryPublishTag,
	selectNarratorAvatarFileForAspect,
	type StoryPublishTag,
} from "@repo/types";
import { db } from "../client";

const DOCUMENT_PATCH_MAX_ATTEMPTS = 8;

export class VideoProjectDocumentConflictError extends Error {
	constructor(message = "Video project document update conflict") {
		super(message);
		this.name = "VideoProjectDocumentConflictError";
	}
}

export const DBVideoProjectService = {
	async create(data: {
		jobId: string;
		organizationId: string;
		createdByUserId: string;
		title: string;
		inputBrief: Record<string, unknown>;
		status?: VideoProjectStatusType;
	}): Promise<VideoProject> {
		const result = await db.videoProject.create({
			data: {
				jobId: data.jobId,
				organizationId: data.organizationId,
				createdByUserId: data.createdByUserId,
				title: data.title,
				inputBrief: data.inputBrief,
				status: data.status ?? VideoProjectStatusSchema.Values.PROCESSING,
				errorMessage: [],
			},
		});
		return VideoProjectSchema.parse(result);
	},

	async update(
		id: string,
		data: {
			title?: string;
			status?: VideoProjectStatusType;
			document?: Record<string, unknown> | null;
			validation?: Record<string, unknown> | null;
			inputBrief?: Record<string, unknown>;
			errorMessage?: string[];
			cost?: number | null;
		},
	): Promise<VideoProject> {
		const result = await db.videoProject.update({
			where: { id },
			data: {
				...(data.title !== undefined ? { title: data.title } : {}),
				...(data.status !== undefined ? { status: data.status } : {}),
				...(data.document !== undefined ? { document: data.document } : {}),
				...(data.validation !== undefined
					? { validation: data.validation }
					: {}),
				...(data.inputBrief !== undefined
					? { inputBrief: data.inputBrief }
					: {}),
				...(data.errorMessage !== undefined
					? { errorMessage: data.errorMessage }
					: {}),
				...(data.cost !== undefined ? { cost: data.cost } : {}),
			},
		});
		return VideoProjectSchema.parse(result);
	},

	/**
	 * Read-modify-write document with optimistic locking on `updatedAt`.
	 * Retries when a concurrent request updates the same project first.
	 */
	async patchDocument(
		id: string,
		patch: (
			document: Record<string, unknown>,
		) => Record<string, unknown> | null | undefined,
		options?: { maxAttempts?: number },
	): Promise<VideoProject> {
		const maxAttempts = options?.maxAttempts ?? DOCUMENT_PATCH_MAX_ATTEMPTS;

		for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
			const project = await this.getById(id);
			if (!project) {
				throw new Error(`Video project not found: ${id}`);
			}

			const currentDocument = (project.document ?? {}) as Record<
				string,
				unknown
			>;
			const nextDocument = patch(currentDocument);
			if (nextDocument == null) return project;

			const result = await db.videoProject.updateMany({
				where: {
					id,
					isDeleted: false,
					updatedAt: project.updatedAt,
				},
				data: {
					document: nextDocument,
				},
			});

			if (result.count === 1) {
				const refreshed = await this.getById(id);
				if (!refreshed) {
					throw new Error(`Video project not found after patch: ${id}`);
				}
				return refreshed;
			}
		}

		throw new VideoProjectDocumentConflictError();
	},

	async getById(id: string): Promise<VideoProject | null> {
		const result = await db.videoProject.findFirst({
			where: { id, isDeleted: false },
			include: { design: true, job: true },
		});
		if (!result) return null;
		return result as unknown as VideoProject;
	},

	async getByJobId(jobId: string): Promise<VideoProject | null> {
		const result = await db.videoProject.findFirst({
			where: { jobId, isDeleted: false },
		});
		if (!result) return null;
		return VideoProjectSchema.parse(result);
	},

	async listByOrganizationId(
		organizationId: string,
		options?: { limit?: number; offset?: number },
	): Promise<{ data: VideoProject[]; total: number }> {
		const where = { organizationId, isDeleted: false };
		const limit = options?.limit ?? 20;
		const offset = options?.offset ?? 0;
		const [total, rows] = await Promise.all([
			db.videoProject.count({ where }),
			db.videoProject.findMany({
				where,
				orderBy: { createdAt: "desc" },
				skip: offset,
				take: limit,
			}),
		]);
		return {
			data: rows.map((row) => VideoProjectSchema.parse(row)),
			total,
		};
	},

	async listPublishedStoryEpisodes(
		organizationId: string,
		options?: { excludeProjectId?: string },
	): Promise<
		Array<{
			id: string;
			title: string;
			document: Record<string, unknown> | null;
			publishTag: unknown;
			episodeSummary: string | null;
			inputBrief: Record<string, unknown>;
		}>
	> {
		const rows = await db.videoProject.findMany({
			where: {
				organizationId,
				isDeleted: false,
				publishedAt: { not: null },
			},
			select: {
				id: true,
				title: true,
				document: true,
				publishTag: true,
				episodeSummary: true,
				inputBrief: true,
			},
			orderBy: { publishedAt: "asc" },
		});
		return rows
			.filter((row) => {
				if (options?.excludeProjectId && row.id === options.excludeProjectId)
					return false;
				return parseStoryPublishTag(row.publishTag)?.type === "story";
			})
			.map((row) => ({
				id: row.id,
				title: row.title,
				document: row.document as Record<string, unknown> | null,
				publishTag: row.publishTag,
				episodeSummary: row.episodeSummary,
				inputBrief: row.inputBrief as Record<string, unknown>,
			}));
	},

	async publishStoryEpisode(
		id: string,
		options?: { episodeNumber?: number },
	): Promise<VideoProject> {
		const project = await this.getById(id);
		if (!project) throw new Error(`Video project not found: ${id}`);

		const inputBrief = project.inputBrief as Record<string, unknown>;
		if (inputBrief.format !== "story") {
			throw new Error("Only story projects can be published as episodes");
		}

		const publishedRows = await this.listPublishedStoryEpisodes(
			project.organizationId,
			{ excludeProjectId: id },
		);
		const episodeNumber =
			options?.episodeNumber ?? nextStoryEpisodeNumber(publishedRows);

		if (episodeNumber < 1 || !Number.isInteger(episodeNumber)) {
			throw new Error("episodeNumber must be a positive integer");
		}

		const taken = publishedRows.some(
			(row) =>
				parseStoryPublishTag(row.publishTag)?.episodeNumber === episodeNumber,
		);
		if (taken) {
			throw new Error(`Episode number ${episodeNumber} is already published`);
		}

		const document = (project.document ?? {}) as Record<string, unknown>;
		const title =
			(typeof document.title === "string" && document.title.trim()) ||
			project.title;
		const logline =
			typeof document.logline === "string" ? document.logline.trim() : "";
		const publishTag: StoryPublishTag = {
			type: "story",
			episodeNumber,
		};
		const episodeSummary = buildEpisodeSummary({ title, logline });

		const result = await db.videoProject.update({
			where: { id },
			data: {
				publishedAt: new Date(),
				publishTag,
				episodeSummary,
			},
		});
		return VideoProjectSchema.parse(result);
	},

	async copyToOrganization(
		id: string,
		targetOrganizationId: string,
		userId: string,
	): Promise<VideoProject> {
		const sourceProject = await this.getById(id);
		if (!sourceProject) throw new Error(`Video project not found: ${id}`);

		const sourceJobId = sourceProject.jobId;
		const inputBrief = (sourceProject.inputBrief ?? {}) as Record<
			string,
			unknown
		>;
		const document = (sourceProject.document ?? {}) as Record<
			string,
			unknown
		>;

		// 1. Create a new Job in the target organization
		const newJob = await db.job.create({
			data: {
				title: sourceProject.title,
				userId,
				organizationId: targetOrganizationId,
				cost: 0,
				status: "COMPLETED",
				functionName: "VideoProject",
				source: "WEB_APP",
				errorMessage: [],
			},
		});

		// 2. Clone all visual project assets (AppFiles) to the target organization
		const fileIdMap = new Map<string, string>();
		if (sourceJobId) {
			const sourceFiles = await db.appFile.findMany({
				where: {
					jobId: sourceJobId,
					type: { in: ["image", "video"] },
				},
			});

			for (const file of sourceFiles) {
				const clonedFile = await db.appFile.create({
					data: {
						name: file.name,
						userId,
						organizationId: targetOrganizationId,
						jobId: newJob.id,
						type: file.type,
						status: file.status,
						description: file.description,
						tags: file.tags,
						size: file.size,
						length: file.length,
						key: file.key,
						thumbnailUrl: file.thumbnailUrl,
					},
				});
				fileIdMap.set(file.id, clonedFile.id);
			}
		}

		// Helper to recursively remap file IDs in JSON structures
		function remapFileIds(val: unknown): unknown {
			if (typeof val === "string") {
				return fileIdMap.get(val) ?? val;
			}
			if (Array.isArray(val)) {
				return val.map(remapFileIds);
			}
			if (val && typeof val === "object") {
				const res: Record<string, unknown> = {};
				for (const [k, v] of Object.entries(val)) {
					res[k] = remapFileIds(v);
				}
				return res;
			}
			return val;
		}

		const nextDocument = remapFileIds(document) as Record<string, unknown>;

		// 3. Reset all dialogue and voiceover audio in the copied document
		if (Array.isArray(nextDocument.beats)) {
			nextDocument.beats = nextDocument.beats.map((beat: unknown) => {
				if (!beat || typeof beat !== "object") return beat;
				const b = { ...(beat as Record<string, unknown>) };

				// Reset beat-level audio
				b.audioUrl = "";
				b.audioAppFileId = undefined;
				b.measuredDurationMs = undefined;
				b.sourceDurationMs = undefined;
				b.captionSegments = undefined;

				if (b.voiceover && typeof b.voiceover === "object") {
					b.voiceover = {
						...(b.voiceover as Record<string, unknown>),
						audioUrl: "",
						audioAppFileId: undefined,
						measuredDurationMs: undefined,
						sourceDurationMs: undefined,
					};
				}

				// Reset dialogue lines audio
				if (Array.isArray(b.dialogue)) {
					b.dialogue = b.dialogue.map((line: unknown) => {
						if (!line || typeof line !== "object") return line;
						return {
							...(line as Record<string, unknown>),
							audioUrl: "",
							audioAppFileId: undefined,
							measuredDurationMs: undefined,
							sourceDurationMs: undefined,
							captionSegments: undefined,
						};
					});
				}

				return b;
			});
		}

		const nextInputBrief = { ...inputBrief };

		// 4. Switch to the target organization's default or active narrator profile and reset avatar appearances
		const targetDefaultProfile =
			(await db.organizationNarratorProfile.findFirst({
				where: {
					organizationId: targetOrganizationId,
					isActive: true,
					isDefault: true,
				},
			})) ||
			(await db.organizationNarratorProfile.findFirst({
				where: {
					organizationId: targetOrganizationId,
					isActive: true,
				},
				orderBy: { createdAt: "asc" },
			}));

		if (targetDefaultProfile) {
			const projectAspect =
				(typeof nextDocument.aspect === "string" &&
					nextDocument.aspect.trim()) ||
				(typeof nextInputBrief.aspect === "string" &&
					nextInputBrief.aspect.trim()) ||
				undefined;

			const ttsProvider =
				targetDefaultProfile.provider === "gemini"
					? "gemini"
					: "openai";
			const newNarrator = narratorConfigFromProfile(
				{
					id: targetDefaultProfile.id,
					narratorName: targetDefaultProfile.narratorName,
					gender:
						targetDefaultProfile.gender === "female"
							? "female"
							: "male",
					provider: ttsProvider,
					voiceId: targetDefaultProfile.voiceId,
					personality: targetDefaultProfile.personality,
					styleNotes: targetDefaultProfile.styleNotes,
					ttsStyle: targetDefaultProfile.ttsStyle,
					ttsPace: targetDefaultProfile.ttsPace,
					ttsAccent: targetDefaultProfile.ttsAccent,
					ttsNotes: targetDefaultProfile.ttsNotes,
					channelName: targetDefaultProfile.channelName,
					channelSignOff: targetDefaultProfile.channelSignOff,
					overlayText: targetDefaultProfile.overlayText,
					overlayTextDelayMs: targetDefaultProfile.overlayTextDelayMs,
					overlayTextDurationMs: targetDefaultProfile.overlayTextDurationMs,
					speechSpeed: targetDefaultProfile.speechSpeed ?? 1,
					avatarFileAppId: targetDefaultProfile.avatarFileAppId,
					avatarPhotos: targetDefaultProfile.avatarPhotos,
				},
				projectAspect,
			);

			const avatarTiming =
				narratorAvatarTimingFromProfile(targetDefaultProfile);
			const newAvatarFileAppId = selectNarratorAvatarFileForAspect(
				targetDefaultProfile,
				projectAspect,
			);

			let avatarUrl = "";
			if (newAvatarFileAppId) {
				const avatarFile = await db.appFile.findFirst({
					where: { id: newAvatarFileAppId },
				});
				if (avatarFile?.thumbnailUrl) {
					avatarUrl = avatarFile.thumbnailUrl;
				}
			}

			nextDocument.narrator = newNarrator;
			nextDocument.narratorAvatar = {
				...avatarTiming,
				appearances: [],
				...(newAvatarFileAppId ? { fileAppId: newAvatarFileAppId } : {}),
				...(avatarUrl ? { url: avatarUrl } : {}),
			};

			nextInputBrief.narratorProfileId = targetDefaultProfile.id;
			nextInputBrief.narrator = newNarrator;

			if (targetDefaultProfile.visualDefaults) {
				nextDocument.visualDefaults =
					targetDefaultProfile.visualDefaults as Record<
						string,
						unknown
					>;
				nextInputBrief.visualDefaults =
					targetDefaultProfile.visualDefaults as Record<
						string,
						unknown
					>;
			}
		} else {
			nextInputBrief.narratorProfileId = undefined;
			if (
				nextDocument.narrator &&
				typeof nextDocument.narrator === "object"
			) {
				(
					nextDocument.narrator as Record<string, unknown>
				).profileId = undefined;
			}
			if (
				nextInputBrief.narrator &&
				typeof nextInputBrief.narrator === "object"
			) {
				(
					nextInputBrief.narrator as Record<string, unknown>
				).profileId = undefined;
			}
			if (
				nextDocument.narratorAvatar &&
				typeof nextDocument.narratorAvatar === "object"
			) {
				(
					nextDocument.narratorAvatar as Record<string, unknown>
				).appearances = [];
			}
		}

		// 5. Create new VideoProject in target organization
		const result = await db.videoProject.create({
			data: {
				jobId: newJob.id,
				organizationId: targetOrganizationId,
				createdByUserId: userId,
				title: sourceProject.title,
				status: VideoProjectStatusSchema.Values.VALIDATED,
				inputBrief: nextInputBrief,
				document: nextDocument,
				errorMessage: [],
			},
		});

		return VideoProjectSchema.parse(result);
	},
};
